import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Menu, X, Package, ShoppingBag, ShoppingCart, Store, Globe, Check, User, ChevronDown, Home, LogOut, LogIn, Gift } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

import CurrencySelector from "@/components/CurrencySelector";
// Inline Language Selector for Navbar
const NavbarLanguageSelector = ({ isScrolled }) => {
  const { language, changeLanguage, languages, currentLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={"flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors " + 
          (isScrolled 
            ? "hover:bg-slate-100 text-foreground" 
            : "hover:bg-secondary/20 text-secondary"
          )}
        data-testid="navbar-language-selector"
        title="Change Language"
      >
        <Globe className="w-4 h-4" />
        <span className="text-base">{currentLanguage?.flag}</span>
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-44 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden animate-scale-in">
            <div className="p-2">
              <p className="px-3 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wide">Language</p>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => { changeLanguage(lang.code); setIsOpen(false); }}
                  className={"w-full px-3 py-2 text-left flex items-center gap-3 rounded-lg transition-colors " + 
                    (language === lang.code 
                      ? "bg-primary/10 text-primary" 
                      : "text-foreground hover:bg-muted"
                    )}
                  data-testid={"lang-" + lang.code}
                >
                  <span className="text-xl">{lang.flag}</span>
                  <span className="flex-1 font-medium text-sm">{lang.name}</span>
                  {language === lang.code && <Check className="h-4 w-4 text-primary" />}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const Navbar = () => {
  const navigate = useNavigate();
  const { cartCount } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const { t } = useLanguage();
  
  const handleSellClick = () => {
    if (isAuthenticated && user) {
      if (user.roles?.includes('MERCHANT') || user.roles?.includes('SELLER')) {
        navigate('/dashboard');
      } else {
        navigate('/seller/apply');
      }
    } else {
      navigate('/login?redirect=/seller/apply');
    }
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Translated nav links
  const navLinks = [
    { label: t('navbar.shop') || 'Shop', href: '/shop' },
    { label: 'Classifieds', href: '/classifieds', isNew: true },
    { label: t('navbar.ship') || 'Ship', href: '#ship' },
    { label: t('navbar.joinUs') || 'Join Us', href: '#join' },
    { label: t('navbar.coverage') || 'Coverage', href: '#coverage' },
  ];

  const handleNavClick = (href) => {
    if (href.startsWith('#')) {
      if (window.location.pathname !== '/welcome' && window.location.pathname !== '/') {
        navigate('/welcome');
        setTimeout(() => {
          const el = document.querySelector(href);
          el?.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      } else {
        const el = document.querySelector(href);
        el?.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(href);
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className={"fixed top-0 left-0 right-0 z-50 transition-all duration-300 " + (isScrolled ? 'bg-background/95 backdrop-blur-md shadow-md py-2' : 'bg-transparent py-4')}>
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between">
          <Link to="/welcome" className="flex items-center gap-3 group">
            <div className={"w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300 shadow-lg group-hover:shadow-gold group-hover:scale-105 " + (isScrolled ? 'bg-gradient-jamaica' : 'bg-secondary')}>
              <Package className={"w-5 h-5 " + (isScrolled ? 'text-primary-foreground' : 'text-secondary-foreground')} />
            </div>
            <div>
              <span className={"font-display text-xl tracking-wide " + (isScrolled ? 'text-foreground' : 'text-secondary')}>LIANTIAO</span>
              <p className={"text-xs font-medium " + (isScrolled ? 'text-muted-foreground' : 'text-secondary/80')}>{t('navbar.tagline') || 'Jamaica Delivery'}</p>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <button 
                key={link.href} 
                onClick={() => handleNavClick(link.href)}
                className={"font-medium text-sm transition-all duration-200 hover:-translate-y-0.5 " + (isScrolled ? 'text-foreground/80 hover:text-primary' : 'text-secondary/90 hover:text-secondary')}
              >
                {link.label}
              </button>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-2">
            {/* Language Selector */}
            <NavbarLanguageSelector isScrolled={isScrolled} />
            <CurrencySelector compact className="ml-2" />
            
            {/* Cart */}
            <Link to="/cart" className="relative p-2 rounded-lg hover:bg-primary/10 transition-colors" data-testid="cart-link">
              <ShoppingCart className={"w-6 h-6 " + (isScrolled ? 'text-foreground' : 'text-secondary')} />
              {cartCount > 0 && (
                <Badge className="absolute -top-1 -right-1 w-5 h-5 p-0 flex items-center justify-center bg-destructive text-destructive-foreground text-xs font-bold">
                  {cartCount > 99 ? '99+' : cartCount}
                </Badge>
              )}
            </Link>
            
            {/* Action Buttons */}
            <Button variant={isScrolled ? "jamaica" : "hero"} size="sm" onClick={() => navigate('/shop')}>
              <ShoppingBag className="w-4 h-4" />{t('navbar.shop') || 'Shop'}
            </Button>
            <Button variant={isScrolled ? "outline" : "heroOutline"} size="sm" className={!isScrolled ? 'border-secondary/60 text-secondary hover:bg-secondary/10' : ''} onClick={handleSellClick}>
              <Store className="w-4 h-4" />{t('navbar.sell') || 'Sell'}
            </Button>
            <Button variant={isScrolled ? "secondary" : "hero"} size="sm" className={isScrolled ? '' : 'bg-accent text-accent-foreground hover:bg-accent/90'} onClick={() => navigate('/send')}>
              <Package className="w-4 h-4" />{t('navbar.ship') || 'Ship'}
            </Button>
            
            {/* Login/Account */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className={isScrolled ? '' : 'text-secondary hover:bg-secondary/10'}>
                    <User className="w-4 h-4" />
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-slate-900 border-slate-700">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} className="cursor-pointer">
                    <User className="w-4 h-4 mr-2" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my-classifieds')} className="cursor-pointer">
                    <Home className="w-4 h-4 mr-2" />
                    My Classifieds
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/refer')} className="cursor-pointer" data-testid="menu-refer-earn">
                    <Gift className="w-4 h-4 mr-2" />
                    Refer &amp; Earn
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-slate-700" />
                  <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} className="cursor-pointer text-red-400">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleLoginClick} 
                  className={`mr-2 ${isScrolled ? '' : 'text-secondary hover:bg-secondary/10'}`}
                >
                  <LogIn className="w-4 h-4 mr-1" />
                  Sign In
                </Button>
                <Button 
                  variant="default" 
                  size="sm" 
                  onClick={() => window.location.href='/signup'}
                  className="bg-secondary text-primary hover:bg-secondary/90"
                >
                  <User className="w-4 h-4 mr-1" />
                  Join
                </Button>
              </>
            )}
          </div>

          {/* Mobile */}
          <div className="flex items-center gap-2 lg:hidden">
            <NavbarLanguageSelector isScrolled={isScrolled} />
            
            <Link to="/cart" className="relative p-2 rounded-lg hover:bg-primary/10 transition-colors" data-testid="cart-link-mobile">
              <ShoppingCart className={"w-6 h-6 " + (isScrolled ? 'text-foreground' : 'text-secondary')} />
              {cartCount > 0 && (
                <Badge className="absolute -top-1 -right-1 w-5 h-5 p-0 flex items-center justify-center bg-destructive text-destructive-foreground text-xs font-bold">
                  {cartCount > 99 ? '99+' : cartCount}
                </Badge>
              )}
            </Link>
            
            <button className="p-2 rounded-lg hover:bg-primary/10 transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              {isMobileMenuOpen 
                ? <X className={"w-6 h-6 " + (isScrolled ? 'text-foreground' : 'text-secondary')} /> 
                : <Menu className={"w-6 h-6 " + (isScrolled ? 'text-foreground' : 'text-secondary')} />
              }
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 p-4 bg-card rounded-2xl shadow-lg animate-scale-in">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <button 
                  key={link.href} 
                  onClick={() => handleNavClick(link.href)}
                  className="px-4 py-3 rounded-xl font-medium text-foreground hover:bg-primary/10 hover:text-primary transition-colors text-left"
                >
                  {link.label}
                </button>
              ))}
              <Link to="/cart" className="px-4 py-3 rounded-xl font-medium text-foreground hover:bg-primary/10 hover:text-primary transition-colors flex items-center gap-2" onClick={() => setIsMobileMenuOpen(false)}>
                <ShoppingCart className="w-5 h-5" /> {t('navbar.cart') || 'Cart'} {cartCount > 0 && <Badge>{cartCount}</Badge>}
              </Link>
              {isAuthenticated && (
                <Link to="/refer" className="px-4 py-3 rounded-xl font-medium text-foreground hover:bg-primary/10 hover:text-primary transition-colors flex items-center gap-2" onClick={() => setIsMobileMenuOpen(false)} data-testid="menu-refer-earn-mobile">
                  <Gift className="w-5 h-5" /> Refer &amp; Earn
                </Link>
              )}
              
              {!isAuthenticated && (
                <Button variant="outline" className="mt-2" onClick={() => { navigate('/login'); setIsMobileMenuOpen(false); }}>
                  <User className="w-4 h-4 mr-2" />{t('navbar.login') || 'Login'}
                </Button>
              )}
              
              <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-border">
                <Button variant="jamaica" size="sm" className="w-full" onClick={() => navigate('/shop')}><ShoppingBag className="w-4 h-4" />{t('navbar.shop') || 'Shop'}</Button>
                <Button variant="outline" size="sm" className="w-full" onClick={handleSellClick}><Store className="w-4 h-4" />{t('navbar.sell') || 'Sell'}</Button>
                <Button variant="secondary" size="sm" className="w-full" onClick={() => navigate('/send')}><Package className="w-4 h-4" />{t('navbar.ship') || 'Ship'}</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
